import type React from "react"
import { Card, CardContent, CardHeader, Typography, Box, LinearProgress, Tooltip } from "@mui/material"
import { CalendarToday, AccessTime } from "@mui/icons-material"
import styles from "./LeaveBalanceCard.module.css"

interface LeaveBalanceCardProps {
  totalDays: number
  usedDays: number
  pendingDays: number
  className?: string
}

const LeaveBalanceCard: React.FC<LeaveBalanceCardProps> = ({ totalDays, usedDays, pendingDays, className = "" }) => {
  const remainingDays = totalDays - usedDays
  const percentageUsed = (usedDays / totalDays) * 100

  return (
    <Card className={`${styles.card} ${className}`}>
      <CardHeader title="Leave Balance" subheader="Your annual leave allocation" />
      <CardContent>
        <Box className={styles.balanceItem}>
          <Box className={styles.balanceLabel}>
            <CalendarToday fontSize="small" className={styles.icon} />
            <Typography variant="body2" color="textSecondary">
              Annual Leave
            </Typography>
          </Box>
          <Tooltip title={`${remainingDays} days remaining out of ${totalDays} total days`} arrow>
            <Typography variant="h5" className={styles.balanceValue}>
              {remainingDays} days
            </Typography>
          </Tooltip>
        </Box>

        <Box className={styles.progressSection}>
          <Box className={styles.progressLabel}>
            <Typography variant="body2">Used</Typography>
            <Typography variant="body2">{usedDays} days</Typography>
          </Box>
          <LinearProgress variant="determinate" value={percentageUsed} color="success" className={styles.progressBar} />
        </Box>

        {pendingDays > 0 && (
          <Box className={styles.pendingSection}>
            <Box className={styles.balanceLabel}>
              <AccessTime fontSize="small" className={styles.iconWarning} />
              <Typography variant="body2" color="textSecondary">
                Pending Approval
              </Typography>
            </Box>
            <Typography variant="body2" className={styles.pendingValue}>
              {pendingDays} days
            </Typography>
          </Box>
        )}

        <Box className={styles.totalSection}>
          <Typography variant="body2" color="textSecondary">
            Total Annual Allocation
          </Typography>
          <Typography variant="body2" fontWeight="medium">
            {totalDays} days
          </Typography>
        </Box>
      </CardContent>
    </Card>
  )
}

export default LeaveBalanceCard
